import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

import { en } from './en';
import { es, type I18nDict } from './es';

// Idioma de la UI. `useI18n()` devuelve el diccionario activo (migración mínima
// desde `es`: `es.x` → `t.x`). La preferencia se persiste en localStorage y se
// refleja en el atributo `lang` de <html>. Español por defecto.

export type Language = 'es' | 'en';

const STORAGE_KEY = 'lang';
const DICTIONARIES: Record<Language, I18nDict> = { es, en };

interface I18nContextValue {
  lang: Language;
  setLanguage: (lang: Language) => void;
  t: I18nDict;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function readStored(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'es' || stored === 'en') return stored;
  } catch {
    /* localStorage no disponible */
  }
  return 'es';
}

export function I18nProvider({ children }: { children: ReactNode }): JSX.Element {
  const [lang, setLang] = useState<Language>(() => readStored());

  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = lang;
  }, [lang]);

  const setLanguage = (next: Language): void => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* localStorage no disponible */
    }
    setLang(next);
  };

  return (
    <I18nContext.Provider value={{ lang, setLanguage, t: DICTIONARIES[lang] }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nDict {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n debe usarse dentro de <I18nProvider>');
  return ctx.t;
}

export function useLanguage(): { lang: Language; setLanguage: (lang: Language) => void } {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useLanguage debe usarse dentro de <I18nProvider>');
  return { lang: ctx.lang, setLanguage: ctx.setLanguage };
}
